import * as React from "react";
import { Link } from "react-router-dom";
import { UsersRound } from "lucide-react";

import { getConnectionSummary } from "@/api/client";
import { IconTooltip, navIconClass } from "@/features/home/components/NavIcons";

/**
 * Header connections link → /connections, with a badge of pending connect
 * requests. Polls on an interval and on tab focus (the same "cheap REST poll"
 * the homepage uses), so the badge stays roughly live without a socket. When a
 * poll shows new activity since the last one — a request just arrived, or one
 * you sent was just accepted — it flashes a small self-contained toast next to
 * the icon.
 *
 * <p>The glyph is a pair of people, not a bell: a bell reads as "notifications",
 * but this is the mutual-connections hub.</p>
 */
export function ConnectionsBell() {
  const [count, setCount] = React.useState(0);
  const [flash, setFlash] = React.useState<string | null>(null);
  const prev = React.useRef<{ pending: number; accepted: number } | null>(null);
  const flashTimer = React.useRef<number | undefined>(undefined);

  const showFlash = React.useCallback((msg: string) => {
    setFlash(msg);
    window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 6000);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    const load = () =>
      getConnectionSummary()
        .then((s) => {
          if (cancelled) return;
          setCount(s.pending);
          const before = prev.current;
          // Only after a first baseline: announce increases, not the initial load.
          if (before) {
            if (s.pending > before.pending) showFlash("New connection request");
            else if (s.accepted > before.accepted) showFlash("Your connection request was accepted");
          }
          prev.current = { pending: s.pending, accepted: s.accepted };
        })
        .catch(() => {
          /* transient — keep the last known state */
        });

    void load();
    const id = window.setInterval(load, 30_000);
    const onFocus = () => void load();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.clearTimeout(flashTimer.current);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [showFlash]);

  const label =
    count > 0 ? `Connections: ${count} pending request${count === 1 ? "" : "s"}` : "Connections";

  return (
    <div className="relative">
      <Link
        to="/connections"
        aria-label={label}
        className="group relative inline-flex h-9 w-9 items-center justify-center rounded-xl text-sky-600 transition-colors hover:bg-muted"
      >
        <UsersRound className={navIconClass} aria-hidden />
        {count > 0 && (
          <span className="absolute right-0.5 top-0.5 inline-flex min-w-[1.05rem] items-center justify-center rounded-full border-2 border-background bg-rose-500 px-1 text-[0.6rem] font-bold leading-4 text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
        <IconTooltip label="Connections" />
      </Link>

      {flash && (
        <Link
          to="/connections"
          role="status"
          onClick={() => setFlash(null)}
          className="absolute right-0 top-full z-40 mt-2 w-56 rounded-md border-2 border-black bg-amber-50 px-3 py-2 text-left text-xs font-semibold text-amber-900 shadow hover:bg-amber-100"
        >
          🔔 {flash}
        </Link>
      )}
    </div>
  );
}
